import React, { useCallback, useMemo, useRef, useState } from 'react';
import ThronVideo from '@components/utils/ThronVideo';
import { useReducedMotion } from 'framer-motion';
import '@stone/scss/components/flp-hero/_flp-hero.scss';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import Image from '@stone/ui/image/Image';
import FlpHeroStickyBar from './FlpHeroStickyBar';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import cookie from 'react-cookies';
import { useTranslation } from 'react-i18next';
import Icon from '@stone/ui/icon/Icon';
import Button from '@stone/ui/button/Button';
import ThronAudioSoundwave from './ThronAudioSoundWave';
import { useLangChecker } from '@common/utils/i18n';
gsap.registerPlugin(ScrollTrigger);
const markers = cookie.load('gsap_markers') === 'flp_hero';
const FlpHero = (props) => {
    var _a;
    // ANCHOR - REFS -------------------------------------------------------------------
    const imageRef = useRef();
    const contentRef = useRef();
    const player = useRef();
    const firstRender = useRef(true);
    // ANCHOR - HOOKS -------------------------------------------------------------------
    const isReduced = useReducedMotion();
    const queries = useMediaQueries();
    const { t } = useTranslation();
    // ANCHOR - USE STATE -------------------------------------------------------------------
    const [isPlaying, setIsPlaying] = useState(!isReduced);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [dialogOpen, setDialogOpena] = useState(null);
    const [timeStatus, setTimeStatus] = useState({
        duration: null,
        currentTime: null,
    });
    // ANCHOR - MEMO -------------------------------------------------------------------
    const isVideo = useMemo(() => {
        return !!props.thronId || !!props.thronIdMobile;
    }, [props]);
    // ANCHOR - FUNCTIONS -------------------------------------------------------------------
    const togglePlaying = useCallback(() => {
        var _a, _b;
        isAudioPlaying ? (_a = player.current) === null || _a === void 0 ? void 0 : _a.pause() : (_b = player.current) === null || _b === void 0 ? void 0 : _b.play();
    }, [isAudioPlaying]);
    useGSAP(() => {
        ScrollTrigger.create({
            id: 'flp-hero',
            trigger: imageRef.current,
            markers: markers,
            pin: true,
            pinSpacing: false,
            anticipatePin: 3,
            scrub: 1,
            start: 'top top',
            end: 'bottom top+=32px',
            endTrigger: imageRef.current.parentElement,
        });
    }, { scope: imageRef });
    // ANCHOR - CONSTS --------------------------------------------------------------------
    const contentsArray = (_a = props.overlayContent) === null || _a === void 0 ? void 0 : _a.split(/\r?\n/);
    const renderAudio = () => (React.createElement("div", { className: "flp-hero-additional__audio" },
        React.createElement(Button, { id: `${props.id}-play-audio`, className: "audiotrack__vawe-play", cls: ['button--quaternary'], type: "button", inverted: true, onClick: togglePlaying },
            React.createElement(React.Fragment, null,
                React.createElement(Icon, { name: isAudioPlaying ? 'speaker-on-white' : 'speaker-off-white', family: "16" }),
                (isAudioPlaying && t('button.pauseshort')) || React.createElement("span", { ref: useLangChecker }, t('button.playshort')))),
        React.createElement(ThronAudioSoundwave, { isPlaying: isAudioPlaying, progress: timeStatus.currentTime / timeStatus.duration, 
            // loop
            thronID: props.audioId, onPauseCallback: () => {
                setIsAudioPlaying(false);
            }, onPlayCallback: () => {
                setIsAudioPlaying(true);
            }, onCompleteCallback: () => {
                setIsAudioPlaying(false);
            }, onInitCallback: (playerInstance) => {
                player.current = playerInstance;
                setTimeStatus(playerInstance.status);
            }, onTimeUpdate: (playerInstance) => setTimeStatus(playerInstance.status), noskin: true })));
    const renderAdditional = () => (React.createElement("div", { className: "flp-hero-additional" },
        React.createElement("div", { className: "container" },
            React.createElement("div", { className: "row" },
                React.createElement("div", { className: "col-lg-4 col-xxl-3 offset-lg-2" },
                    React.createElement("p", { className: "font__title flp-hero-additional__title" },
                        React.createElement("span", null, props.overlayTitle),
                        React.createElement("span", null, "\u2013"))),
                React.createElement("div", { className: "col-lg-10 col-xxl-6" },
                    React.createElement("p", null, props.overlayDescription)),
                (contentsArray === null || contentsArray === void 0 ? void 0 : contentsArray.length) && (React.createElement("div", { className: "col-lg-4 col-xxl-6 offset-lg-3 offset-xxl-6" },
                    React.createElement("p", { className: "font__title flp-hero-additional__content" },
                        React.createElement("span", null, props.overlayContentTitle)),
                    props.type === 'FLP' && (React.createElement("ul", { className: "flp-hero-additional__list" }, contentsArray.map((el, i) => (React.createElement("li", { key: `content-item-${i}` },
                        React.createElement("p", { className: "font__title" }, el)))))),
                    props.type === 'ARCHIVE' && (React.createElement(React.Fragment, null,
                        React.createElement("p", null, props.overlayContent),
                        renderAudio()))))))));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flp-hero-bgimage", ref: imageRef }, isVideo ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "video-container" },
                React.createElement(ThronVideo, { autoplay: !isReduced, thronID: props.thronId, thronMobileID: props.thronIdMobile, lockBitrate: props === null || props === void 0 ? void 0 : props.maxBitrate, onPauseCallback: () => setIsPlaying(false), onPlayCallback: () => setIsPlaying(true) }),
                React.createElement("div", { className: "btn--container" },
                    React.createElement("button", { id: "flp-hero-video-button", className: "visually-hidden", "data-pause": true, "aria-pressed": "false", "aria-hidden": "true", tabIndex: -1 }))))) : (React.createElement(Image, { src: props.image, srcMobile: props === null || props === void 0 ? void 0 : props.imageMobile, alt: props.altImage }))),
        React.createElement("div", { className: "flp-hero-content", ref: contentRef },
            React.createElement(FlpHeroStickyBar, Object.assign({}, props, { queries: queries, showMarkers: markers, contentRef: contentRef, setDialogOpen: setDialogOpena, dialogOpen: dialogOpen, isAudioPlaying: isAudioPlaying, audioStatus: timeStatus }))),
        renderAdditional(),
        isVideo && (React.createElement("div", { className: "btn--container container" },
            React.createElement("button", { className: "button button--pause inverted", "aria-label": t('button.pause'), onClick: () => {
                    const button = document.querySelector('button#flp-hero-video-button');
                    button.click();
                } },
                React.createElement(Icon, { family: "16", name: isPlaying ? 'pause' : 'play', inverted: true, autoSize: true }),
                React.createElement("span", null, isPlaying ? t('button.pause') : t('button.play')))))));
};
export default FlpHero;
